import { useState, useEffect } from "react";
import {
	SiReact,
	SiNextdotjs,
	SiTypescript,
	SiJavascript,
	SiTailwindcss,
	SiDaisyui,
	SiRedux,
	SiChartdotjs,
	SiNodedotjs,
	SiExpress,
	SiSupabase,
	SiMongodb,
	SiMysql,
	SiPostgresql,
	SiFirebase,
	SiVercel,
	SiCloudflarepages,
	SiRender,
	// SiAmazonwebservices,
	// SiLinode,
	SiMapbox,
	SiUmami,
	SiPosthog,
	SiSimpleanalytics,
	// SiSearchconsole,
	SiGit,
	SiPostman,
} from "react-icons/si";

import favicon1 from "url:../../images/favicon1.ico";
import favicon2 from "url:../../images/favicon2.ico";
import favicon3 from "url:../../images/favicon3.ico";
import awsIcon from "url:../../images/aws.ico";
import linodeIcon from "url:../../images/linode.ico";
import seoIcon from "url:../../images/google-search-console-icon.png";
import dodopayIcon from "url:../../images/dodopayments.ico";

const ICON_MAP = {
	React: <SiReact className="text-[#61DAFB]" />,
	"Next.js": <SiNextdotjs />,
	TypeScript: <SiTypescript className="text-[#3178C6]" />,
	JavaScript: <SiJavascript className="text-[#F7DF1E]" />,
	"Tailwind CSS": <SiTailwindcss className="text-[#06B6D4]" />,
	DaisyUI: <SiDaisyui className="text-[#FF9903]" />,
	Redux: <SiRedux className="text-[#764ABC]" />,
	"Chart.js": <SiChartdotjs className="text-[#FF6384]" />,
	"Node.js": <SiNodedotjs className="text-[#339933]" />,
	"Express.js": <SiExpress />,
	Supabase: <SiSupabase className="text-[#3ECF8E]" />,
	MongoDB: <SiMongodb className="text-[#47A248]" />,
	MySQL: <SiMysql className="text-[#4479A1]" />,
	PostgreSQL: <SiPostgresql className="text-[#4169E1]" />,
	Firebase: <SiFirebase className="text-[#FFCA28]" />,
	Vercel: <SiVercel />,
	"Cloudflare Pages": <SiCloudflarepages className="text-[#F38020]" />,
	Render: <SiRender />,
	// AWS: <SiAmazonwebservices className="text-[#FF9900]" />,
	// Linode: <SiLinode className="text-[#00A95C]" />,
	Mapbox: <SiMapbox />,
	Umami: <SiUmami className="text-[#3E82F7]" />,
	PostHog: <SiPosthog />,
	"Simple Analytics": <SiSimpleanalytics className="text-[#FF4F00]" />,
	SEO: <img src={seoIcon} alt="SEO" className="w-4 h-4" />,
	"Dodo Payments": (
		<img src={dodopayIcon} alt="Dodo Payments" className="w-4 h-4" />
	),
	AWS: <img src={awsIcon} alt="AWS" className="w-4 h-4" />,
	Linode: <img src={linodeIcon} alt="Linode" className="w-4 h-4" />,
	Git: <SiGit className="text-[#F05032]" />,
	Postman: <SiPostman className="text-[#FF6C37]" />,
};

const NAV_LINKS = ["projects", "skills", "experience", "contact"];

const PROJECTS = [
	{
		icon: favicon1,
		name: "FocusLive",
		url: "focuslive.app",
		href: "https://focuslive.app",
		badge: "Real-time Focus Platform",
		badgeIcon: "🌐",
		desc: "Pomodoro app with a live Mapbox globe showing active users worldwide — deep work without feeling isolated. Monetised with Dodo Payments.",
		tags: ["Next.js", "Supabase", "Mapbox", "Vercel", "Dodo Payments"],
		stat: "~6 organic visits/day · SEO + blogs + Product Hunt",
		color: "#1A56DB",
	},
	{
		icon: favicon2,
		name: "Quizingo",
		url: "quizingo.app",
		href: "https://quizingo.app",
		badge: "Quizzing Platform",
		badgeIcon: "🧩",
		desc: "Community quizzing platform with timed grid-based quizzes (3×3 – 5×5). Shareable emoji result cards built for social virality.",
		tags: ["React", "Supabase", "Google Auth", "PostHog", "CF Pages"],
		stat: "Active via Reddit · PostHog analytics",
		color: "#7C3AED",
	},
	{
		icon: favicon3,
		name: "TestCrunch",
		url: "testcrunch.in",
		href: "https://testcrunch.in",
		badge: "Aptitude Test Preparation Platform",
		badgeIcon: "📊",
		desc: "Aptitude test prep app with a static question bank, timed sessions, and Chart.js performance graphs. First project I independently shipped.",
		tags: ["React", "Chart.js", "Tailwind", "DaisyUI", "Render"],
		stat: "First shipped app · Simple Analytics",
		color: "#059669",
	},
];

const SKILLS = [
	{
		label: "Frontend",
		icon: "⌨",
		items: [
			"React",
			"Next.js",
			"TypeScript",
			"JavaScript",
			"Tailwind CSS",
			"DaisyUI",
			"Redux",
			"Chart.js",
		],
	},
	{
		label: "Backend & DB",
		icon: "⚙",
		items: [
			"Node.js",
			"Express.js",
			"Supabase",
			"MongoDB",
			"MySQL",
			"PostgreSQL",
			"Firebase",
		],
	},
	{
		label: "Cloud & Hosting",
		icon: "☁",
		items: ["Vercel", "Cloudflare Pages", "Render", "AWS", "Linode", "Mapbox"],
	},
	{
		label: "Analytics & Growth",
		icon: "📈",
		items: [
			"Umami",
			"PostHog",
			"Simple Analytics",
			"SEO",
			"Dodo Payments",
			"Git",
			"Postman",
		],
	},
];

const EXPERIENCE = [
	{
		date: "Jun 2024 – Present",
		role: "Freelance Developer",
		company: "Self-Employed",
		bullets: [
			"Shipped three production apps — FocusLive, Quizingo, TestCrunch — each independently conceived, built, and deployed.",
			"Managed full product lifecycle: development, deployment, SEO, analytics, and payments integration (Dodo Payments).",
		],
	},
	{
		date: "Jun 2019 – Jun 2024",
		role: "Frontend Developer & Admin Support",
		company: "Family Business",
		bullets: [
			"Built a web-based inventory and billing management interface using the MERN stack.",
			"Managed digital records, stock tracking, and vendor coordination.",
		],
	},
	{
		date: "Jun 2018 – May 2019",
		role: "Trainee Software Engineer",
		company: "Mastek",
		bullets: [
			"Built a Leave Management App — reduced system response time by 80%.",
			"Implemented ARIA accessibility standards; improved usability for visually impaired users by 63%.",
			"Refactored legacy codebase to reduce technical debt; improved test coverage by 26%.",
		],
	},
	{
		date: "Aug 2014 – May 2018",
		role: "B.E. in Information Technology",
		company: "K. J. Somaiya Institute of Engineering & IT",
		bullets: ["CGPI: 7.85"],
	},
];

const CONTACT = [
	{
		icon: "✉",
		label: "email",
		value: "karanmundra97@gmail.com",
		href: "mailto:karanmundra97@gmail.com",
	},
	{
		icon: "in",
		label: "linkedin",
		value: "karanmundra",
		href: "https://linkedin.com/in/karanmundra",
	},
	{ icon: "📍", label: "location", value: "Mumbai, Maharashtra", href: null },
];

function useActiveSection() {
	const [active, setActive] = useState("projects");
	useEffect(() => {
		const els = NAV_LINKS.map((id) => document.getElementById(id)).filter(
			Boolean,
		);
		const obs = new IntersectionObserver(
			(entries) => {
				entries.forEach((e) => {
					if (e.isIntersecting) setActive(e.target.id);
				});
			},
			{ rootMargin: "-40% 0px -55% 0px" },
		);
		els.forEach((el) => obs.observe(el));
		return () => obs.disconnect();
	}, []);
	return active;
}

function Cursor() {
	const [on, setOn] = useState(true);
	useEffect(() => {
		const t = setInterval(() => setOn((v) => !v), 530);
		return () => clearInterval(t);
	}, []);
	return (
		<span
			className={`inline-block w-0.5 h-[1em] ml-0.5 align-middle rounded-sm transition-colors duration-100 ${
				on ? "bg-[#1A56DB] dark:bg-[#4d8ef7]" : "bg-transparent"
			}`}
		/>
	);
}

export default function Portfolio() {
	const active = useActiveSection();
	const [copied, setCopied] = useState(false);

	const copyEmail = () => {
		navigator.clipboard.writeText("karanmundra97@gmail.com");
		setCopied(true);
		setTimeout(() => setCopied(false), 1800);
	};

	return (
		<div className="min-h-screen bg-white text-[#0f1117] dark:bg-[#0d0f14] dark:text-[#e8eaf0] font-sans antialiased selection:bg-[#1A56DB]/20">
			{/* NAV */}
			<nav className="sticky top-0 z-50 h-14 px-4 sm:px-8 flex items-center justify-between bg-white/80 dark:bg-[#0d0f14]/80 backdrop-blur-md border-b border-black/[0.08] dark:border-white/[0.07]">
				<span className="font-mono text-sm font-medium text-[#1A56DB] dark:text-[#4d8ef7] tracking-tight">
					<a
						href="/"
						onClick={(e) => {
							e.preventDefault();
							window.history.pushState(null, "", "/");
							window.scrollTo({ top: 0, behavior: "smooth" });
						}}
					>
						km
						<Cursor />
					</a>
				</span>
				<ul className="flex items-center list-none">
					{NAV_LINKS.map((id) => (
						<li key={id}>
							<a
								href={`#${id}`}
								className={`font-mono text-xs px-2.5 py-1.5 sm:px-3.5 rounded-md transition-all duration-150 capitalize ${
									active === id
										? "text-[#1A56DB] dark:text-[#4d8ef7] bg-[#1A56DB]/10 dark:bg-[#4d8ef7]/12"
										: "text-[#9ca3af] dark:text-[#4b5568] hover:text-[#1A56DB] dark:hover:text-[#4d8ef7] hover:bg-[#1A56DB]/5"
								}`}
							>
								{id}
							</a>
						</li>
					))}
				</ul>
			</nav>

			{/* MAIN CONTAINER */}
			<main className="max-w-3xl mx-auto px-4 sm:px-8 pt-16 pb-24">
				{/* HERO */}
				<section className="mb-20 pb-16 border-b border-black/[0.08] dark:border-white/[0.07]">
					<p className="font-mono text-xs text-[#1A56DB]/85 dark:text-[#4d8ef7]/85 mb-4">
						// hello world
					</p>
					<h1 className="text-4xl sm:text-5xl md:text-6xl font-light tracking-tight leading-tight mb-2">
						<strong className="font-medium">Karan</strong> Mundra
					</h1>
					<p className="font-mono text-xs sm:text-sm text-[#9ca3af] dark:text-[#4b5568] tracking-wider mb-6">
						frontend / fullstack developer
					</p>
					<p className="text-base text-[#4b5563] dark:text-[#8b92a8] font-light leading-relaxed max-w-xl mb-8">
						I build and ship production web apps — from real-time globes to
						quizzing platforms. MERN + Next.js stack, with a habit of actually
						deploying things and watching the analytics.
					</p>
					<div className="flex flex-wrap gap-2.5">
						<button
							className={`inline-flex items-center gap-1.5 font-mono text-xs px-4 py-2.5 rounded-lg border transition-all duration-150 shadow-sm ${
								copied
									? "bg-green-600 border-green-600 text-white"
									: "bg-[#1A56DB] border-[#1A56DB] text-white hover:bg-[#1245b0] dark:bg-[#4d8ef7] dark:border-[#4d8ef7] dark:hover:bg-[#6ba3f8]"
							}`}
							onClick={copyEmail}
						>
							{copied ? "✓ copied" : "✉ get in touch"}
						</button>
						{PROJECTS.map((p) => (
							<a
								key={p.name}
								className="inline-flex items-center gap-1.5 font-mono text-xs px-4 py-2.5 rounded-lg border border-black/14 bg-[#f7f8fa] text-[#4b5563] hover:border-[#1A56DB] hover:text-[#1A56DB] hover:bg-[#1A56DB]/10 dark:border-white/12 dark:bg-[#13161d] dark:text-[#8b92a8] dark:hover:border-[#4d8ef7] dark:hover:text-[#4d8ef7] dark:hover:bg-[#4d8ef7]/12 transition-all duration-150"
								href={p.href}
								target="_blank"
								rel="noreferrer"
							>
								↗ {p.url}
							</a>
						))}
					</div>
				</section>

				{/* SECTIONS GRID BLOCK */}
				{[
					{
						id: "projects",
						label: "projects",
						content: (
							<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3.5">
								{PROJECTS.map((p) => (
									<div
										className="group relative flex flex-col bg-[#f7f8fa] dark:bg-[#13161d] border border-black/[0.08] dark:border-white/[0.07] rounded-xl p-5 pt-6 transition-all duration-200 hover:-translate-y-0.5 hover:border-black/14 dark:hover:border-white/12 overflow-hidden"
										key={p.name}
									>
										<div
											className="absolute top-0 left-0 right-0 h-[2px]"
											style={{ backgroundColor: p.color }}
										/>
										<div className="flex items-start justify-between mb-3">
											<div className="flex items-center gap-2">
												<img
													src={p.icon}
													alt={p.name}
													className="w-6 h-6 rounded"
												/>
												<span className="text-sm font-medium">{p.name}</span>
											</div>
											<a
												className="text-xs text-[#9ca3af] dark:text-[#4b5568] hover:text-[#1A56DB] dark:hover:text-[#4d8ef7] transition-colors duration-150 leading-none mt-0.5"
												href={p.href}
												target="_blank"
												rel="noreferrer"
												aria-label={`Open ${p.name}`}
											>
												↗
											</a>
										</div>
										<div className="inline-flex items-center gap-1.5 font-mono text-[10.5px] px-2.5 py-0.5 rounded bg-[#1A56DB]/10 text-[#1A56DB] dark:bg-[#4d8ef7]/12 dark:text-[#4d8ef7] self-start mb-3.5">
											<span>{p.badgeIcon}</span>
											{p.badge}
										</div>
										<p className="text-xs text-[#4b5563] dark:text-[#8b92a8] font-light leading-relaxed mb-4 flex-1">
											{p.desc}
										</p>
										<div className="flex flex-wrap gap-1 mb-3.5">
											{p.tags.map((t) => (
												<span
													className="font-mono text-[9px] px-1.5 py-0.5 rounded bg-[#f0f1f4] text-[#9ca3af] dark:bg-[#1a1d26] dark:text-[#4b5568] border border-black/[0.08] dark:border-white/[0.07]"
													key={t}
												>
													{t}
												</span>
											))}
										</div>
										<p className="font-mono text-[10.5px] text-[#9ca3af] dark:text-[#4b5568] pt-3 border-t border-black/[0.08] dark:border-white/[0.07] leading-normal">
											<span className="text-[#1A56DB] dark:text-[#4d8ef7] mr-1">
												↗
											</span>
											{p.stat}
										</p>
									</div>
								))}
							</div>
						),
					},
					{
						id: "skills",
						label: "skills",
						content: (
							<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
								{SKILLS.map((g) => (
									<div
										className="bg-[#f7f8fa] dark:bg-[#13161d] border border-black/[0.08] dark:border-white/[0.07] rounded-xl p-4"
										key={g.label}
									>
										<p className="font-mono text-xs text-[#9ca3af] dark:text-[#4b5568] mb-3 flex items-center gap-1.5 tracking-wide uppercase">
											<span className="text-sm">{g.icon}</span>
											{g.label}
										</p>
										<div className="flex flex-wrap gap-1.5">
											{g.items.map((s) => (
												<span
													className="text-xs px-2 py-1 rounded-md bg-white border border-black/[0.08] text-[#4b5563] dark:bg-[#0d0f14] dark:border-white/[0.07] dark:text-[#8b92a8] flex items-center gap-1.5"
													key={s}
												>
													{ICON_MAP[s] || null}
													{s}
												</span>
											))}
										</div>
									</div>
								))}
							</div>
						),
					},
					{
						id: "experience",
						label: "experience",
						content: (
							<div className="flex flex-col">
								{EXPERIENCE.map((e) => (
									<div
										className="grid grid-cols-1 md:grid-cols-[148px_1fr] gap-1.5 md:gap-6 py-5 border-b last:border-none border-black/[0.08] dark:border-white/[0.07]"
										key={e.role}
									>
										<p className="font-mono text-[10.5px] text-[#9ca3af] dark:text-[#4b5568] md:pt-1 leading-normal">
											{e.date}
										</p>
										<div>
											<p className="text-sm font-medium">{e.role}</p>
											<p className="font-mono text-xs text-[#1A56DB] dark:text-[#4d8ef7] mb-2.5">
												{e.company}
											</p>
											<ul className="list-none flex flex-col gap-1.5">
												{e.bullets.map((b) => (
													<li
														className="relative text-xs text-[#4b5563] dark:text-[#8b92a8] font-light leading-relaxed pl-4 before:content-['▸'] before:absolute before:left-0 before:text-[#1A56DB] dark:before:text-[#4d8ef7] before:text-[10px] before:top-0"
														key={b}
													>
														{b}
													</li>
												))}
											</ul>
										</div>
									</div>
								))}
							</div>
						),
					},
					{
						id: "contact",
						label: "contact",
						content: (
							<div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
								{CONTACT.map((c) => {
									const InnerContent = () => (
										<>
											<span className="font-mono text-base font-medium text-[#1A56DB] dark:text-[#4d8ef7] w-5 text-center flex-shrink-0">
												{c.icon}
											</span>
											<div>
												<span className="block font-mono text-[10px] text-[#9ca3af] dark:text-[#4b5568] tracking-wider uppercase mb-0.5">
													{c.label}
												</span>
												<span className="text-xs font-normal text-[#0f1117] dark:text-[#e8eaf0] truncate block max-w-[180px]">
													{c.value}
												</span>
											</div>
										</>
									);

									return c.href ? (
										<a
											className="flex items-center gap-3 p-3.5 border border-black/[0.08] dark:border-white/[0.07] rounded-xl bg-[#f7f8fa] dark:bg-[#13161d] hover:border-[#1A56DB] dark:hover:border-[#4d8ef7] hover:bg-[#1A56DB]/5 dark:hover:bg-[#4d8ef7]/12 transition-all duration-150"
											href={c.href}
											key={c.label}
											target={c.href.startsWith("http") ? "_blank" : undefined}
											rel="noreferrer"
										>
											<InnerContent />
										</a>
									) : (
										<div
											className="flex items-center gap-3 p-3.5 border border-black/[0.08] dark:border-white/[0.07] rounded-xl bg-[#f7f8fa] dark:bg-[#13161d]"
											key={c.label}
										>
											<InnerContent />
										</div>
									);
								})}
							</div>
						),
					},
				].map((section) => (
					<section
						className="mb-16 scroll-mt-20"
						id={section.id}
						key={section.id}
					>
						<p className="font-mono text-[11px] text-[#9ca3af] dark:text-[#4b5568] tracking-[0.12em] uppercase mb-6 flex items-center gap-2.5 before:content-[''] before:w-5 before:h-[1px] before:bg-[#1A56DB] dark:before:bg-[#4d8ef7]">
							{section.label}
						</p>
						{section.content}
					</section>
				))}

				{/* <iframe
					src="https://www.quizingo.app/game/I2BKHX"
					width="100%"
					height="600"
					frameBorder="0"
					allow="fullscreen"
				></iframe> */}

				{/* FOOTER */}
				<footer className="text-center font-mono text-[11px] text-[#9ca3af] dark:text-[#4b5568] pt-8 border-t border-black/[0.08] dark:border-white/[0.07]">
					<p>
						built with React & Tailwind · designed by Karan Mundra ·{" "}
						{new Date().getFullYear()}
					</p>
				</footer>
			</main>
		</div>
	);
}
